import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  Erreur,
  Etiquette,
  Nombre,
  Vide,
} from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';

/**
 * Catégories de produit — RG-020.
 *
 * Une catégorie ne se SUPPRIME jamais : `produit.categorie_id` est NOT NULL,
 * et l'effacer rendrait orphelin tout ce qu'elle range. On la désactive : elle
 * disparaît des listes de saisie sans toucher aux fiches existantes.
 *
 * L'écran affiche le nombre de produits concernés avant de désactiver — c'est
 * l'information qui manque toujours au moment de décider.
 */

interface Categorie {
  id: string;
  nom: string;
  actif: boolean;
  produits: number;
}

export function Categories(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { peut } = useSession();
  const client = useQueryClient();

  const [nom, setNom] = useState('');
  const [inclureInactives, setInclureInactives] = useState(false);
  const [renommee, setRenommee] = useState<{ id: string; nom: string } | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['categories', inclureInactives],
    queryFn: () =>
      appeler<{ donnees: Categorie[] }>(
        `/categories${inclureInactives ? '?inactives=1' : ''}`,
      ),
    select: (reponse) => reponse.donnees,
  });

  function rafraichir(): void {
    void client.invalidateQueries({ queryKey: ['categories'] });
    // Le formulaire de produit lit le même référentiel : sans cela, une
    // catégorie neuve n'apparaîtrait qu'au rechargement de la page.
    void client.invalidateQueries({ queryKey: ['referentiel-catalogue'] });
  }

  const creation = useMutation({
    mutationFn: (valeur: string) =>
      appeler<Categorie>('/categories', {
        methode: 'POST',
        corps: { id: crypto.randomUUID(), nom: valeur },
      }),
    onSuccess: () => {
      setNom('');
      rafraichir();
    },
  });

  const renommage = useMutation({
    mutationFn: ({ id, valeur }: { id: string; valeur: string }) =>
      appeler<Categorie>(`/categories/${id}`, {
        methode: 'PATCH',
        corps: { nom: valeur },
      }),
    onSuccess: () => {
      setRenommee(null);
      rafraichir();
    },
  });

  const activite = useMutation({
    mutationFn: ({ id, actif }: { id: string; actif: boolean }) =>
      appeler(`/categories/${id}/${actif ? 'reactivation' : 'desactivation'}`, {
        methode: 'POST',
      }),
    onSuccess: rafraichir,
  });

  const modifiable = peut('categorie.gerer');

  function soumettre(evenement: SyntheticEvent): void {
    evenement.preventDefault();
    creation.mutate(nom);
  }

  return (
    <>
      <Entete
        titre="Catégories"
        sous="Un produit appartient à exactement une catégorie."
      />

      <Erreur erreur={error} />
      <Erreur erreur={activite.error} />
      <Erreur erreur={renommage.error} />

      <div className="pile">
        {modifiable && (
          <Carte titre="Nouvelle catégorie">
            <form onSubmit={soumettre} className="ligne">
              <div className="champ" style={{ margin: 0, flex: 1, minWidth: 220 }}>
                <label htmlFor="nom-categorie">Nom</label>
                <input
                  id="nom-categorie"
                  name="nom"
                  required
                  minLength={2}
                  placeholder="Boissons, Épicerie sèche…"
                  value={nom}
                  onChange={(evenement) => {
                    setNom(evenement.target.value);
                  }}
                />
              </div>
              <button type="submit" disabled={creation.isPending}>
                {creation.isPending ? 'Création…' : 'Créer'}
              </button>
            </form>
            <Erreur erreur={creation.error} />
          </Carte>
        )}

        <Carte>
          <label style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              style={{ width: 'auto', marginRight: 8 }}
              checked={inclureInactives}
              onChange={(evenement) => {
                setInclureInactives(evenement.target.checked);
              }}
            />
            Afficher aussi les catégories désactivées
          </label>

          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucune catégorie. Créez-en une avant d’ajouter des produits." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Catégories',
                        nomFichier: 'categories',
                        colonnes: [
                          { cle: 'nom', entete: 'Nom', rendu: () => null, texte: (c: Categorie) => c.nom },
                          {
                            cle: 'produits',
                            entete: 'Produits',
                            numerique: true,
                            rendu: () => null,
                            texte: (c: Categorie) => String(c.produits),
                          },
                          {
                            cle: 'etat',
                            entete: 'État',
                            rendu: () => null,
                            texte: (c: Categorie) => (c.actif ? 'Active' : 'Désactivée'),
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Produits</th>
                    <th>État</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((categorie) => (
                    <tr key={categorie.id}>
                      <td>
                        {renommee?.id === categorie.id ? (
                          <input
                            aria-label={`Renommer ${categorie.nom}`}
                            autoFocus
                            value={renommee.nom}
                            onChange={(evenement) => {
                              setRenommee({
                                id: categorie.id,
                                nom: evenement.target.value,
                              });
                            }}
                            onKeyDown={(evenement) => {
                              if (evenement.key === 'Enter') {
                                renommage.mutate({
                                  id: categorie.id,
                                  valeur: renommee.nom,
                                });
                              }
                              if (evenement.key === 'Escape') {
                                setRenommee(null);
                              }
                            }}
                          />
                        ) : (
                          categorie.nom
                        )}
                      </td>
                      <td>
                        <Nombre valeur={categorie.produits} />
                      </td>
                      <td>
                        {categorie.actif ? (
                          <Etiquette ton="vert" texte="active" />
                        ) : (
                          <Etiquette ton="rouge" texte="désactivée" />
                        )}
                      </td>
                      <td>
                        {modifiable && (
                          <div className="ligne">
                            {renommee?.id === categorie.id ? (
                              <button
                                type="button"
                                className="bouton--discret"
                                onClick={() => {
                                  renommage.mutate({
                                    id: categorie.id,
                                    valeur: renommee.nom,
                                  });
                                }}
                              >
                                Enregistrer
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="bouton--discret"
                                onClick={() => {
                                  setRenommee({ id: categorie.id, nom: categorie.nom });
                                }}
                              >
                                Renommer
                              </button>
                            )}
                            <button
                              type="button"
                              className="bouton--discret"
                              disabled={activite.isPending}
                              onClick={() => {
                                activite.mutate({
                                  id: categorie.id,
                                  actif: !categorie.actif,
                                });
                              }}
                            >
                              {categorie.actif ? 'Désactiver' : 'Réactiver'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}
